<style>
.memo-popover {
    width: 40vw; /* or any fixed width like 400px */
    max-width: 90vw;
}
.allergy-popover {
    width: 40vw;
    max-width: 90vw;
}
</style>
<template>
    <b-navbar id="schedule-button" class="justify-content-between border-top border-secondary" type="light" variant="white">
        <b-navbar-nav>
            <b-button
            size="sm"
            variant="info"
            :to="patientLink"
            >
            <i class="fa-regular fa-user"></i> <span class="d-md-down-none">View Patient</span>
            </b-button>
        </b-navbar-nav>
        <b-navbar-nav class="flex-column align-items-start">
            <span
                id="popover-memo"
                @click="popoverShow = true"
                class="text-truncate align-middle plain-text-underline"
                style="max-width: 40vw;">
                <small>
                    <i class="d-md-down-none text-muted fa-regular fa-memo-circle-info mr-2"></i> {{  memo?.description ?  memo.description : 'No memo.'}}
                </small>
            </span>
            <b-popover
                target="popover-memo"
                triggers="click"
                :show.sync="popoverShow"
                placement="bottom"
                container="schedule-buttons"
                ref="popover"
                custom-class="memo-popover">
                    <template #title>
                        <b-button @click="popoverShow = false" class="close" aria-label="Close">
                            <span class="d-inline-block" aria-hidden="true">&times;</span>
                        </b-button>
                        Memo
                    </template>
                <MemoForm @saved="popoverShow = false" />
            </b-popover>
            <span
                id="popover-allergy"
                @click="allergyPopoverShow = true"
                class="text-truncate align-middle plain-text-underline"
                style="max-width: 40vw;">
                <small>
                    <i class="d-md-down-none fa-regular fa-triangle-exclamation mr-2" :class="allergies.length ? 'text-danger' : 'text-muted'"></i>
                    <template v-if="!allergies.length">No known allergies.</template>
                    <template v-else>
                        <template v-for="(item, index) in allergyItems">
                            <span :key="`name-${index}`">{{ item.name }}</span><span v-if="item.reaction" :key="`reaction-${index}`" class="text-danger"> ({{ item.reaction }})</span><span v-if="index < allergyItems.length - 1" :key="`sep-${index}`">, </span>
                        </template>
                    </template>
                </small>
            </span>
            <b-popover
                target="popover-allergy"
                triggers="click"
                :show.sync="allergyPopoverShow"
                placement="bottom"
                container="schedule-buttons"
                ref="allergyPopover"
                custom-class="allergy-popover">
                    <template #title>
                        <b-button @click="allergyPopoverShow = false" class="close" aria-label="Close">
                            <span class="d-inline-block" aria-hidden="true">&times;</span>
                        </b-button>
                        Allergies
                    </template>
                <div v-if="!allergies.length" class="small text-muted text-center my-2">No known allergies.</div>
                <b-table v-else class="mb-0" thead-class="bg-secondary" borderless striped small responsive :items="allergies" :fields="allergyFields" sort-by="identification_date" sort-desc />
            </b-popover>
        </b-navbar-nav>
        <div class="d-flex">
            <b-navbar-nav class="mr-3">
                <template v-if="!patientProcessed">
                <b-overlay
                    :show="$store.getters['loading/isLoading']('schedule_arrived')"
                    rounded
                    opacity="0.6"
                    spinner-small
                    :spinner-variant="patientArrived ? 'success' : 'primary'"
                    class="d-inline-block"
                    >
                    <b-button
                    v-if="!patientArrived"
                    size="sm"
                    variant="primary"
                    @click="$emit('arrived', true)"
                    >
                    <i class="fa-regular fa-circle-play"></i> <span class="d-md-down-none">Arrived</span>
                    </b-button>

                    <b-button
                    v-else
                    size="sm"
                    variant="outline-success"
                    @click="$emit('arrived', false)"
                    >
                    <i class="fa-regular fa-circle-check"></i> <span class="d-md-down-none">Undo Arrived</span>
                    </b-button>
                </b-overlay>
                </template>
            </b-navbar-nav>

            <b-navbar-nav>
                <b-overlay
                    :show="$store.getters['loading/isLoading']('schedule')"
                    rounded
                    opacity="0.6"
                    spinner-small
                    :spinner-variant="canProcess ? 'success' : 'warning'"
                    class="d-inline-block">

                    <b-button v-if="!patientProcessed" size="sm" :disabled="!canProcess" variant="success" @click="$emit('process', true)">
                    <i class="fa-regular fa-circle-check"></i> <span class="d-md-down-none">Treated</span>
                    </b-button>

                    <b-button-group size="sm">
                    <b-button v-if="patientProcessed" :disabled="!canUnProcess" variant="outline-warning" @click="$emit('process', false)">
                    <i class="fa-regular fa-xmark"></i> <span class="d-md-down-none">Undo Treated</span>
                    </b-button> 

                    <b-button v-if="patientProcessed" disabled class="d-md-down-none" variant="warning">
                        <span class="small">{{ patientUpdated }}</span>
                    </b-button>
                    </b-button-group>
                </b-overlay>
            </b-navbar-nav>
        </div>
    </b-navbar>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapState, mapActions } from "vuex"
import MemoForm from "@/core/components/patients/memo/MemoForm"

export default FormExtension.extend({
    name: 'schedule-navbar',
    components: {
        MemoForm
    },
    data() {
        return {
            popoverShow: false,
            allergyPopoverShow: false,
            allergyFields: [
                { key: 'identification_date', label: 'ID Date', class: 'small', formatter: (value) => this.toLocaleDate(value) },
                { key: 'allergy_code.name', label: 'Name', class: 'small' },
                { key: 'allergy_severity.name', label: 'Severity', class: 'small' },
                { key: 'reaction', label: 'Reaction', class: 'small' },
            ]
        }
    },
    computed: {
        ...mapGetters('schedule', ['getPatientArrived', 'getConsultHasPassed', 'getPatientProcessed', 'getCanProcessPatient', 'getCurrentConsultation',]),
        patientProcessed() {
            return this.getPatientProcessed()
        },
        patientArrived() {
            return this.getPatientArrived()
        },
        canProcess() {
            return this.getCanProcessPatient()
        },
        canUnProcess() {
            return !this.getConsultHasPassed()
        },
        ...mapGetters('schedule', ['getCurrentConsultation']),
        consultation() {
            return this.getCurrentConsultation()
        },
        patientUpdated() {
            return this.consultation?.updated 
              ? `${this.toLocaleDateTime(this.consultation.updated)} - ${this.consultation.updated_by?.name}` 
              : ""
        },
        patientLink() {
            return this.checkUserPermission('patient', 'can_change') ? `/patients/${this.patient?.id}/edit` : `/patients/${this.patient?.id}/view`
        },
        ...mapGetters('patients', ['getPatientMemo']),
        memo() {
            return this.getPatientMemo()
        },
        ...mapGetters('health', ['getPatientAllergies']),
        allergies() {
            return this.getPatientAllergies() || []
        },
        allergyItems() {
            return this.allergies
                .map(a => ({ name: a.allergy_code?.name, reaction: a.reaction }))
                .filter(item => item.name)
        }
    },
    methods: {
        ...mapActions('patients', ['getMemoForCurrentPatient']),
        ...mapActions('health', ['getAllergiesForCurrentPatient']),
        async loadForPatient(patient) {
            await Promise.all([
                this.getMemoForCurrentPatient(),
                this.getAllergiesForCurrentPatient()
            ])
        },
    }
})

</script>